import type { ConsoleTab, EscalationLevel } from '@badger-sentinel/rules';
import type {
  CellOrdersState,
  GameStatus,
  GmAction,
  MwReleasedPayload,
  MwStagePayload,
  OrderDraft,
  PhaseState,
  SeatIdentity,
  SideBrandingSet,
  TeamOrdersState,
} from '@badger-sentinel/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AlertLane } from './chrome/AlertLane.js';
import { ConfirmInline } from './chrome/ConfirmInline.js';
import { ContextMenu } from './chrome/ContextMenu.js';
import { OtherView } from './chrome/OtherView.js';
import { GmControlStrip } from './chrome/GmControlStrip.js';
import { IdentityRow } from './chrome/IdentityRow.js';
import { ReadOnlyBanner } from './chrome/ReadOnlyBanner.js';
import { StatusBar } from './chrome/StatusBar.js';
import { PHASE_LABELS, StatusRow } from './chrome/StatusRow.js';
import { TabStrip } from './chrome/TabStrip.js';
import { Toast, TOAST_TOP } from './chrome/Toast.js';
import { useMwMapAvailable } from './maps/MwMap.js';
import { MwView } from './mw/MwView.js';
import { IntelView } from './intel/IntelView.js';
import { useScenarioFacade, type ScenarioFacadeState } from './maps/useScenarioFacade.js';
import { CyberView } from './cyber/CyberView.js';
import { SdaView } from './sda/SdaView.js';
import { TheatreView } from './theatre/TheatreView.js';
import { QueueDock } from './orders/QueueDock.js';
import { RosterRail } from './rails/RosterRail.js';
import { SelectionRail } from './rails/SelectionRail.js';
import { alertOverflow, alertsFrom } from './model/alerts.js';
import {
  applyPick,
  filterPickRows,
  pickRowsFor,
  targetForSubject,
  SLOT_ORDER,
  type PickRow,
  type SlotId,
} from './model/builder.js';
import { contextMenuFor } from './model/context-menu.js';
import { alignFeedback, toDrafts } from './model/queue.js';
import { railLayout } from './model/rails.js';
import type { CyberLayerKey, MapLayerKey, SpaceLayerKey } from './model/map-layers.js';
import { canPlaceOnCyber, canPlaceOnOrbit, canPlaceOnTheatre } from './model/scenario.js';
import { countdownFor } from './model/countdown.js';
import { linkFor } from './model/connection.js';
import { projectEscalation } from './model/escalation.js';
import { hasOpenTracks } from './model/mw.js';
import { commitFor, teamReadiness, type OrdersCount } from './model/orders.js';
import { paramSpecOf } from './model/missions.js';
import { resolveTab } from './model/tabs.js';
import { useConsoleStore, type ContextSubject, type MapCommandRequest } from './state/store.js';
import { useViewportWidth } from './useViewportWidth.js';
import type { CellViewWire, IntelReportWire } from '@badger-sentinel/protocol';
import { zoneOf } from './model/roster.js';
import { selectionForId, type Selection } from './model/selection.js';
import { readerTurn, stepTurn } from './model/intel.js';
import type { IntelArchiveState } from './model/intel-archive.js';
import { ClassificationBar } from '../game/chrome.js';
import type { SeatError, SeatGmMessage, SeatSocketStatus } from '../ws/useSeatSocket.js';
import type { BsTheme } from '../theme/useTheme.js';
import { CONSOLE_MIN_VIEWPORT } from './viewport.js';

export { CONSOLE_MIN_VIEWPORT };

/** A stable empty list, so the tab strip's memo is not defeated by a new array. */
const EMPTY_TABS: readonly ConsoleTab[] = Object.freeze([]);

/**
 * The console shell — the frame every seat page sits in (shell.md).
 *
 * > **The shell owns what is true of your seat. A window owns what is true of
 * > your cell's job.**
 *
 * Presentational (M5-D2): every prop is a protocol type or a thin derivation of
 * one, and it emits callbacks. No fetch, no socket, no token — those stay in the
 * application, which is what makes the whole frame renderable from a fixture.
 *
 * **What it deliberately does not take is `CellView`.** The snapshot is replaced
 * wholesale on every resolution and every intra-turn change, so chrome driven by
 * it flickers on every turn advance. The shell reads `session.hello`,
 * `phase.changed`, `orders.state` and *slices* of the view — the escalation pair
 * and the capability pair — and nothing else.
 */

export interface ConsoleShellProps {
  readonly seat: SeatIdentity;
  readonly sides: SideBrandingSet;
  readonly scenario: string;
  readonly phase: PhaseState | null;

  /**
   * Server time now, already corrected (§8.2). The shell never sees the offset
   * and never reads a clock — `useServerNow` is the one place that does, so
   * there is nothing here that *could* render a local timestamp.
   */
  readonly serverNowMs: number;

  readonly connection: SeatSocketStatus;
  readonly roundTripMs: number | null;

  /** The two theatre fields the chrome needs. Not the theatre block, and not the view. */
  readonly escalation: { readonly counter: number; readonly level: EscalationLevel } | null;
  readonly capabilities: {
    readonly readOnly: boolean;
    readonly tabs: readonly ConsoleTab[];
  } | null;

  /** Q21: the denominator is orderable friendly assets for this seat. */
  readonly orderableCount: number | null;
  readonly orders: CellOrdersState | null;
  readonly team: TeamOrdersState | null;

  readonly errors: readonly SeatError[];
  readonly gmMessages: readonly SeatGmMessage[];

  /**
   * Stages that have landed this window, newest last (`mw.stage`).
   *
   * A *slice* like the rest of the chrome's inputs, not the board — the
   * authoritative picture is `view.mwTracks`, which arrives in the snapshot that
   * follows every stage. This exists so the shell can tell that data **arrived**
   * rather than that it is present, which is the only thing that can honestly
   * raise the tab strip's attention dot. Cleared on `phase.changed` by the socket,
   * because a stage belongs to the window that produced it.
   */
  readonly mwStages: readonly MwStagePayload[];

  /**
   * Releases that have landed this window (`mw.released`), newest last.
   *
   * A *slice* like `mwStages` beside it, and it exists for the seat that has no
   * board to see it on: the server pushes this to FIRES and OW, and neither has
   * an MW tab (RB §2.4). `view.mwReleases` carries the same releases and is what
   * the target picker draws its rows from — this is the arrival, which is what
   * the alert lane can honestly raise. Cleared on `phase.changed` by the socket,
   * because the intercept it announces is only composable in that window.
   */
  readonly mwReleases: readonly MwReleasedPayload[];

  readonly theme: BsTheme;
  readonly onToggleTheme: () => void;
  /**
   * Give up the seat and go back to entry.
   *
   * App-level, like `onToggleTheme` beside it and threaded the same way: the
   * shell knows nothing about tokens or stored sessions, it just says when the
   * operator asked. Phase 9 moved the control here from the retired harness's
   * body — a seat needs a way out that does not depend on which tab is open, and
   * "always available regardless of what you are looking at" is the definition of
   * chrome this file already uses for the alert lane.
   */
  readonly onLeave: () => void;
  readonly onSubmit: (submitted: boolean) => void;
  /**
   * The whole cell's draft, never a delta (`zOrdersDraftPayload`).
   *
   * Called with everything the queue holds on every local edit. Idempotent by
   * construction, so a reconnect needs no reconciliation — the client sends what
   * it wants stored and the server stores exactly that (§8.5).
   */
  readonly onDraft: (turn: number, orders: readonly OrderDraft[]) => void;
  /**
   * IDENTIFY / RELEASE / DISMISS (`mw.action`).
   *
   * A **command, not an order** (M4-D8): it mutates the live world and another
   * seat sees it immediately. No stage and no timestamp go with it — both are the
   * server's, because a client that could name its own release stage could claim
   * the stage-1 credit at any moment (§8.3).
   */
  readonly onMwAction: (
    eventId: string,
    action: 'IDENTIFY' | 'RELEASE' | 'DISMISS',
    identity?: string,
  ) => void;
  /**
   * `publish.contact` — an SDA or ISR seat handing a contact to its team
   * (RB §2.4, §5.8.4).
   *
   * A **command, not an order**, on the same footing as `onMwAction`: it changes
   * the live world now, the server re-snapshots the whole team on it, and there
   * is nothing to queue. The seat check is the server's
   * (`capabilitiesOf(cell).canPublish`); the console reads the same bit off
   * `view.capabilities` so that the control is absent rather than rejected.
   */
  readonly onPublish: (contact: string) => void;
  readonly gm: {
    readonly busy: boolean;
    readonly onControl: (action: GmAction, extendMs?: number) => void;
  } | null;

  /**
   * The rails' slice, and the one place `CellView` reaches the shell.
   *
   * The chrome above still takes protocol *slices* and never the view — that is
   * what stops the identity row, the countdown and the commit control
   * re-rendering on every resolution. The rails genuinely consume the whole
   * picture (thirty friendly fields, every contact, the map and the theatre), so
   * they take it, and it stops at them: nothing between here and `StatusRow`
   * reads it.
   */
  readonly view: CellViewWire | null;

  /**
   * The live intel report — the newest resolved turn's, from `intel.report`.
   *
   * `report.turn` is the turn the report is **about**, which is `phase.turn − 1`
   * and never `phase.turn`. Viewer-guarded in the app on the same footing as the
   * view (`state/guard.ts`), because a report is the other document the
   * projection stamps a seat on.
   */
  readonly intel: IntelReportWire | null;
  /**
   * Older turns, by turn number (M5 Q18's REST archive).
   *
   * A map rather than a fetcher, because the shell is presentational (M5-D2):
   * the seat token stays in the app and so does the call it authorises. Absent
   * turns are `idle` — the reader asks for what it needs and nothing else.
   */
  readonly intelArchive: ReadonlyMap<number, IntelArchiveState>;
  readonly onRequestIntel: (turn: number) => void;
}

export function ConsoleShell(props: ConsoleShellProps): JSX.Element {
  const selectedTab = useConsoleStore((state) => state.selectedTab);
  const selectTab = useConsoleStore((state) => state.selectTab);
  const hotkeysOpen = useConsoleStore((state) => state.hotkeysOpen);
  const setHotkeysOpen = useConsoleStore((state) => state.setHotkeysOpen);
  const dismissedAlerts = useConsoleStore((state) => state.dismissedAlerts);
  const dismissAlert = useConsoleStore((state) => state.dismissAlert);
  const toast = useConsoleStore((state) => state.toast);
  const clearToast = useConsoleStore((state) => state.clearToast);
  const selection = useConsoleStore((state) => state.selection);
  const select = useConsoleStore((state) => state.select);
  const pins = useConsoleStore((state) => state.pins);
  const togglePin = useConsoleStore((state) => state.togglePin);
  const batch = useConsoleStore((state) => state.batch);
  const toggleBatch = useConsoleStore((state) => state.toggleBatch);
  const clearBatch = useConsoleStore((state) => state.clearBatch);
  const setCompareOpen = useConsoleStore((state) => state.setCompareOpen);
  const compareOpen = useConsoleStore((state) => state.compareOpen);
  const mwAttention = useConsoleStore((state) => state.mwAttention);
  const raiseMwAttention = useConsoleStore((state) => state.raiseMwAttention);
  const clearMwAttention = useConsoleStore((state) => state.clearMwAttention);
  const intelTurn = useConsoleStore((state) => state.intelTurn);
  const readIntelTurn = useConsoleStore((state) => state.readIntelTurn);
  const intelAttention = useConsoleStore((state) => state.intelAttention);
  const noteIntelReport = useConsoleStore((state) => state.noteIntelReport);
  const clearIntelAttention = useConsoleStore((state) => state.clearIntelAttention);
  const contextMenu = useConsoleStore((state) => state.contextMenu);
  const openContextMenu = useConsoleStore((state) => state.openContextMenu);
  const closeContextMenu = useConsoleStore((state) => state.closeContextMenu);
  const mapLayers = useConsoleStore((state) => state.mapLayers);
  const spaceLayers = useConsoleStore((state) => state.spaceLayers);
  const cyberLayers = useConsoleStore((state) => state.cyberLayers);
  const toggleMapLayer = useConsoleStore((state) => state.toggleMapLayer);
  const toggleSpaceLayer = useConsoleStore((state) => state.toggleSpaceLayer);
  const toggleCyberLayer = useConsoleStore((state) => state.toggleCyberLayer);
  const commandMap = useConsoleStore((state) => state.commandMap);
  const cycleFacet = useConsoleStore((state) => state.cycleFacet);
  const clearFacets = useConsoleStore((state) => state.clearFacets);
  const railLeftOpen = useConsoleStore((state) => state.railLeftOpen);
  const railRightOpen = useConsoleStore((state) => state.railRightOpen);
  const railLeftWidth = useConsoleStore((state) => state.railLeftWidth);
  const railRightWidth = useConsoleStore((state) => state.railRightWidth);
  const builder = useConsoleStore((state) => state.builder);
  const builderOpen = useConsoleStore((state) => state.builderOpen);
  const setBuilderOpen = useConsoleStore((state) => state.setBuilderOpen);
  const setBuilder = useConsoleStore((state) => state.setBuilder);
  const armedSlot = useConsoleStore((state) => state.armedSlot);
  const armSlot = useConsoleStore((state) => state.armSlot);
  const pickQuery = useConsoleStore((state) => state.pickQuery);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillMission = useConsoleStore((state) => state.fillMission);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const fillParam = useConsoleStore((state) => state.fillParam);
  const ladderOpen = useConsoleStore((state) => state.ladderOpen);
  const setLadderOpen = useConsoleStore((state) => state.setLadderOpen);
  const queue = useConsoleStore((state) => state.queue);
  const queueRevision = useConsoleStore((state) => state.queueRevision);
  const queueDraft = useConsoleStore((state) => state.queueDraft);
  const editOrder = useConsoleStore((state) => state.editOrder);
  const duplicateOrder = useConsoleStore((state) => state.duplicateOrder);
  const reassignOrder = useConsoleStore((state) => state.reassignOrder);
  const moveQueued = useConsoleStore((state) => state.moveQueued);
  const setWhyKey = useConsoleStore((state) => state.setWhyKey);
  const whyKey = useConsoleStore((state) => state.whyKey);
  const confirm = useConsoleStore((state) => state.confirm);
  const requestConfirm = useConsoleStore((state) => state.requestConfirm);
  const clearConfirm = useConsoleStore((state) => state.clearConfirm);
  const sends = useConsoleStore((state) => state.sends);
  const noteSend = useConsoleStore((state) => state.noteSend);
  const pruneSends = useConsoleStore((state) => state.pruneSends);
  const syncTurn = useConsoleStore((state) => state.syncTurn);
  const adoptAccepted = useConsoleStore((state) => state.adoptAccepted);
  const undoLast = useConsoleStore((state) => state.undoLast);
  const showToast = useConsoleStore((state) => state.showToast);

  /*
    The receipt `PUBLISH TO TEAM` never had, and the guard against sending twice.

    The commit above got this argument first and it applies here harder: publishing
    hands a contact to seven other seats and cannot be taken back, while submitting
    orders can be withdrawn until the window closes. What the seat saw was a button
    disappearing — which is also what a missed click looks like — so the answer to
    "did that go?" was the room, not the console.

    The in-flight set is keyed on the contact, and every fresh view clears it: the
    server re-snapshots the team on a successful publish, so the control is gone by
    the time the lock lifts, and a publish the server refused unlocks on the next
    snapshot instead of stranding the contact behind a button that no longer works.
  */
  const publishing = useRef<Set<string>>(new Set());
  useEffect(() => {
    publishing.current.clear();
  }, [props.view]);

  const publishContact = useCallback(
    (contact: string): void => {
      if (publishing.current.has(contact)) return;
      publishing.current.add(contact);
      props.onPublish(contact);
      showToast('PUBLISHED · ' + contact, 'ok');
    },
    [props.onPublish, showToast],
  );

  // The clamp runs against the live viewport on every render, so a rail dragged
  // wide while its neighbour was collapsed gives the width back the moment the
  // neighbour reopens — and a window dragged narrow never leaves the centre pane
  // under its floor (`model/rails.ts`).
  const rails = railLayout({
    viewportWidth: useViewportWidth(),
    leftOpen: railLeftOpen,
    rightOpen: railRightOpen,
    leftWidth: railLeftWidth,
    rightWidth: railRightWidth,
  });

  /**
   * The scenario facade and its geometry (M5-D8, M5-D11).
   *
   * Held here rather than in the theatre pane because `mw-map.js` extends
   * `region-map` and boots through the same `import('./scenario.js')` — a model
   * adopted when a tab mounted would go away when that tab did, and take the
   * ground out from under the warning board.
   */
  const facade = useScenarioFacade(props.view, props.scenario);
  const theatre = facade.theatre;

  const countdown = countdownFor(props.phase, props.serverNowMs);
  const tabs = props.capabilities?.tabs ?? [];
  const activeTab = resolveTab(tabs, selectedTab);
  const link = linkFor(props.connection, props.roundTripMs);

  /**
   * Whether the MW board is drawing the selected track's reading itself.
   *
   * True only on the MW tab and only while its map is not mounted, which is the
   * arrangement that leaves the pane a free column. The shell owns the answer
   * because it is the only place that knows both halves, and both rails and the
   * pane have to agree about it or the reading is drawn twice or not at all.
   */
  const mwMapAvailable = useMwMapAvailable();
  const mwReadingInPane = activeTab === 'MW' && !mwMapAvailable;

  /**
   * Which canvas is on screen and drawing — the one fact four things downstream
   * need, decided once.
   *
   * `F`, FOCUS ON MAP, the canvas menu and the layer switches all act on *a* map,
   * and the two maps do not answer the same questions: the theatre places a
   * launcher and cannot place a satellite, `fitSide` exists on one of them, and
   * the layer tables are different lists. Deriving it in four places is how they
   * come to disagree.
   */
  const canvas: 'theatre' | 'space' | 'cyber' | null =
    activeTab === 'THEATRE' && theatre.status === 'ready'
      ? 'theatre'
      : activeTab === 'SDA' && facade.model !== null
        ? 'space'
        : activeTab === 'CYBER' && facade.model !== null
          ? 'cyber'
          : null;

  const commit = commitFor({
    orders: props.orders,
    phase: props.phase?.phase ?? null,
    // RUNNING only. A pause is not an open window with the clock stopped —
    // `assertOrdersWindowOpen` refuses a submit whenever the status is anything
    // but RUNNING, so offering the control during a pause means every click
    // returns WINDOW_CLOSED into the alert lane.
    windowOpen: props.phase?.status === 'RUNNING',
    readOnly: props.capabilities?.readOnly ?? false,
    capabilitiesKnown: props.capabilities !== null,
    // M4-D13, read from the same predicate the server filters on. Without it the
    // MW seat is shown an enabled SUBMIT ORDERS whose only outcome is
    // `MW_TRACKS_OPEN` in the alert lane — Phase 1's finding 11, in a second place.
    mwTracksOpen: hasOpenTracks(props.view),
  });

  // An absent `orders.state` means the server has stored nothing for this cell
  // this turn, which is zero orders — not an unknown number. The server reads
  // it the same way (`orders?.submitted ?? false`), and `—/02` on a fresh turn
  // reads as a console that has not loaded rather than a turn not yet started.
  // The *denominator* is the one that must not be guessed: without a view there
  // is no orderable set, and the whole readout waits.
  const ordersCount: OrdersCount | null =
    props.orderableCount === null
      ? null
      : { tasked: props.orders?.accepted.length ?? 0, orderable: props.orderableCount };

  const projection = useMemo(
    () =>
      props.escalation === null || props.orders === null
        ? null
        : projectEscalation(
            props.escalation.counter,
            props.orders.accepted.map((order) => order.mission),
          ),
    [props.escalation, props.orders],
  );

  /**
   * The three effects that keep the queue and the server agreeing.
   *
   * They are separate on purpose. A turn advance **clears**; a server echo the
   * client did not cause **adopts**; a local edit **sends**. Folding any two of
   * them together produces a loop — adopt-then-send is a console that re-sends
   * its own echo forever, and it looks like a working console right up until the
   * window closes.
   */
  const turn = props.phase?.turn ?? null;
  useEffect(() => {
    if (turn !== null) syncTurn(turn);
  }, [turn, syncTurn]);

  const ordersState = props.orders;
  useEffect(() => {
    if (ordersState === null) return;
    adoptAccepted(ordersState.turn, ordersState.accepted);
  }, [ordersState, adoptAccepted]);

  const sentRevision = useRef(0);
  const onDraft = props.onDraft;
  useEffect(() => {
    if (queueRevision === 0 || turn === null) return;
    if (sentRevision.current === queueRevision) return;
    sentRevision.current = queueRevision;
    const orders = toDrafts(queue);
    noteSend({ seq: queueRevision, keys: queue.map((entry) => entry.key), orders });
    onDraft(turn, orders);
  }, [queueRevision, queue, turn, noteSend, onDraft]);

  const feedback = useMemo(() => alignFeedback(sends, props.orders), [sends, props.orders]);
  const matchedSeq = feedback.matchedSeq;
  useEffect(() => {
    if (matchedSeq !== null) pruneSends(matchedSeq);
  }, [matchedSeq, pruneSends]);

  /**
   * The tab strip's attention dot (design 149–156), which Phase 1 refused to draw.
   *
   * Three rules, and the third is the one that keeps it honest:
   *
   *  - a **new** stage raises it, and only while the operator is looking
   *    somewhere else — a row landing in front of them needs no marker;
   *  - entering the tab clears it;
   *  - `phase.changed` clears it, because the socket empties `mwStages` there and
   *    a stage belongs to the window that produced it. A dot still lit next turn
   *    would point at a board that no longer exists.
   */
  const arrivals = props.mwStages.length;
  const seenArrivals = useRef(0);
  useEffect(() => {
    const previous = seenArrivals.current;
    seenArrivals.current = arrivals;
    if (arrivals === 0) {
      clearMwAttention();
      return;
    }
    if (arrivals > previous && activeTab !== 'MW') raiseMwAttention();
  }, [arrivals, activeTab, raiseMwAttention, clearMwAttention]);

  useEffect(() => {
    if (activeTab === 'MW') clearMwAttention();
  }, [activeTab, clearMwAttention]);

  /**
   * The same dot, raised by intel — and **two** of the three rules rather than
   * three.
   *
   * A stage arrival belongs to the window that produced it, so `phase.changed`
   * clears `mwStages` and the dot with it. A *report* does not go away when the
   * next window opens: it is the record of the turn that just resolved and it is
   * the thing the operator has not read yet. Worse, the report and the phase
   * change arrive together — clearing on `phase.changed` would put the dot out
   * in the same instant it was lit.
   *
   * So the guard is the report's **turn**, held in the store: a re-render, a
   * resume replay or a second snapshot carrying the same report cannot re-raise a
   * dot the operator has already cleared.
   */
  const reportTurn = props.intel?.turn ?? null;
  const lookingAtIntel = activeTab === 'INTEL';
  useEffect(() => {
    if (reportTurn === null) return;
    noteIntelReport(reportTurn, lookingAtIntel);
  }, [reportTurn, lookingAtIntel, noteIntelReport]);

  useEffect(() => {
    if (activeTab === 'INTEL') clearIntelAttention();
  }, [activeTab, clearIntelAttention]);

  /**
   * The tabs wanting attention. `TabStrip` takes a set and knows nothing about
   * which module raised what, so a second raiser needed no change to it at all —
   * which is what Phase 4 predicted when it wrote the component that way.
   */
  const attention = useMemo(() => {
    if (!mwAttention && !intelAttention) return EMPTY_TABS;
    const tabsWanting: ConsoleTab[] = [];
    if (mwAttention) tabsWanting.push('MW');
    if (intelAttention) tabsWanting.push('INTEL');
    return tabsWanting;
  }, [mwAttention, intelAttention]);

  const alertSources = {
    errors: props.errors,
    gmMessages: props.gmMessages,
    status: props.connection,
    mwReleases: props.mwReleases,
    countdown,
    turn: props.phase?.turn ?? null,
    dismissed: dismissedAlerts,
  };
  const alerts = alertsFrom(alertSources);
  const overflow = alertOverflow(alertSources);

  const armedRows: readonly PickRow[] =
    armedSlot === null || props.view === null
      ? []
      : filterPickRows(pickRowsFor(props.view, builder, armedSlot), pickQuery);

  const takePickRow = useCallback(
    (row: PickRow): void => {
      applyPick(row, {
        asset: (id) => fillSlot('asset', id),
        mission: fillMission,
        target: fillTarget,
        param: (value) => {
          const spec = builder.mission === null ? null : paramSpecOf(builder.mission);
          if (spec !== null) fillParam(spec.key, value);
        },
      });
    },
    [builder.mission, fillSlot, fillMission, fillTarget, fillParam],
  );

  /**
   * What `F` would reveal, or `null` — in which case the key stays unbound.
   *
   * The console's standing rule for keys (`p` needs a selection, `c` needs a
   * pin): a key bound to a control that cannot act teaches the operator the
   * console is unresponsive. `F` needs a subject the theatre map can place, a
   * seat that has the tab, and a map that is drawing.
   */
  const mapReveal: { readonly tab: ConsoleTab; readonly command: MapCommandRequest } | null =
    selection === null || props.view === null
      ? null
      : revealOf(props.view, selection, tabs, theatre);

  /**
   * `←` older, `→` newer.
   *
   * Unlike Phases 6 and 7's canvas keys these are **ours**, not a vendored
   * element's, so they go in `BOUND_HOTKEYS` rather than `CANVAS_HOTKEYS` — and
   * the honesty check in `order-hotkeys.test.tsx` is what would catch putting
   * them in the wrong list.
   *
   * `readerTurn` and `stepTurn` are the same pair the pane uses, so the key and
   * the buttons cannot disagree about which turn is next.
   */
  const stepReport = useCallback(
    (direction: -1 | 1): void => {
      const currentTurn = props.phase?.turn ?? null;
      const next = stepTurn(readerTurn(intelTurn, currentTurn), currentTurn, direction);
      if (next === null) return;
      // The newest turn is stored as `null` — *follow the newest* — so stepping
      // forward onto it puts the reader back on the live report rather than
      // pinning it to a number that goes stale next resolution.
      readIntelTurn(currentTurn !== null && next === currentTurn - 1 ? null : next);
    },
    [props.phase, intelTurn, readIntelTurn],
  );

  useHotkeys({
    hotkeysOpen,
    setHotkeysOpen,
    onToggleTheme: props.onToggleTheme,
    contextMenuOpen: contextMenu !== null,
    closeContextMenu,
    selection,
    clearSelection: () => select(null),
    togglePin,
    // `c` needs something pinned to compare. Without that guard it is a key
    // bound to a control that renders nothing, which is the reading Phase 1
    // kept the map down to three keys to avoid.
    toggleCompare: pins.length === 0 ? null : () => setCompareOpen(!compareOpen),
    tabs,
    selectTab,
    // Every builder key is gated on the seat being able to commit — false for a
    // read-only seat and outside an open window (Q20, and Phase 1's finding 11).
    // The strip itself stays on screen in both cases, deliberately (§3.3
    // invariant 3): what is withdrawn is the commit, and a key that fills slots
    // towards a queue the seat may not add to is a control that does nothing.
    builder: commit.enabled
      ? {
          open: builderOpen,
          setOpen: setBuilderOpen,
          armedSlot,
          armSlot,
          query: pickQuery,
          rows: armedRows,
          takeRow: takePickRow,
          queue: () => {
            if (queueDraft()) showToast('ORDER QUEUED');
          },
          applyBatch:
            batch.length > 0 && builder.mission !== null
              ? () => requestConfirm({ kind: 'batch-apply' })
              : null,
        }
      : null,
    ladderOpen,
    setLadderOpen,
    whyOpen: whyKey !== null,
    closeWhy: () => setWhyKey(null),
    confirmOpen: confirm !== null,
    clearConfirm,
    compareOpen,
    closeCompare: () => setCompareOpen(false),
    batchArmed: batch.length > 0,
    clearBatch,
    // The design's `focusUnit`: switch to the tab that owns the subject, then let
    // the map centre, un-stack and flash it.
    focusOnMap:
      mapReveal === null
        ? null
        : () => {
            // The design's `focusUnit`: switch to the board that owns the
            // subject first, then let its canvas centre, un-stack and flash it.
            if (activeTab !== mapReveal.tab) selectTab(mapReveal.tab);
            commandMap(mapReveal.command);
          },
    undo: () => {
      const label = undoLast();
      showToast(label === null ? 'NOTHING TO UNDO' : 'UNDONE · ' + label, 'warn');
    },
    // `←` / `→` walk the archive, and they are bound only where there is an
    // archive to walk: the INTEL tab, with a resolved turn to step to. The
    // console's standing rule for keys — `p` needs a selection, `c` needs a pin,
    // `f` needs a board that can place the subject — held against a fifth.
    stepReport: activeTab === 'INTEL' ? stepReport : null,
  });

  const status: GameStatus | null = props.phase?.status ?? null;
  const isGm = props.gm !== null;
  const [alertLaneHeight, setAlertLaneHeight] = useState(0);

  const menuModel =
    contextMenu === null
      ? null
      : contextMenuFor(contextMenu, {
          view: props.view,
          queue,
          pins,
          batch,
          hasSelection: selection !== null,
          // The canvas items and the layer switches act on a map that is on
          // screen and drawing. On any other tab, and on a theatre with no pack,
          // there is nothing for them to act on and they are not offered.
          map:
            canvas === 'theatre'
              ? { kind: 'theatre', layers: mapLayers }
              : canvas === 'space'
                ? { kind: 'space', layers: spaceLayers }
                : canvas === 'cyber'
                  ? { kind: 'cyber', layers: cyberLayers }
                  : null,
        });

  /**
   * What a context item does.
   *
   * The menu is a primitive that knows nothing about its items (Phases 3–5 are
   * its other callers), so the actions live here — one place, beside the store
   * writes they perform.
   */
  const onContextChoose = (itemId: string): void => {
    if (contextMenu === null) return;
    const id = contextMenu.id;
    // The five layer switches carry their key in the item id, so the menu stays a
    // table (`MAP_LAYER_LABELS`) rather than five near-identical cases here.
    if (itemId.startsWith('layer:')) {
      const key = itemId.slice('layer:'.length);
      // The menu was built from the table for the board on screen, so the key is
      // that board's — there is no third place where the two could be confused.
      if (canvas === 'space') toggleSpaceLayer(key as SpaceLayerKey);
      else if (canvas === 'cyber') toggleCyberLayer(key as CyberLayerKey);
      else toggleMapLayer(key as MapLayerKey);
      return;
    }
    switch (itemId) {
      case 'select':
        select(selectionForMenu(props.view, contextMenu.subject, id));
        return;
      case 'filter-zone': {
        // One zone, armed on its own: the design's "FILTER ROSTER TO X" replaces
        // whatever was armed rather than adding to it, because it is a jump to a
        // place and not another condition on the last search.
        clearFacets();
        cycleFacet('ZONE', zoneForMenu(props.view, contextMenu.subject, id) ?? id);
        return;
      }
      case 'pin': {
        const pin = selectionForMenu(props.view, contextMenu.subject, id);
        if (pin !== null) togglePin(pin);
        return;
      }
      case 'batch':
        toggleBatch(id);
        return;
      case 'clear-selection':
        select(null);
        return;
      case 'clear-batch':
        clearBatch();
        return;

      // --- the map ------------------------------------------------------
      case 'focus-map':
        commandMap({ kind: 'UNIT', id });
        return;
      case 'publish':
        publishContact(id);
        return;
      case 'zoom-zone':
        commandMap({ kind: 'REGION', id });
        return;
      case 'zoom-band':
        commandMap({ kind: 'BAND', id });
        return;
      case 'zoom-network':
        commandMap({ kind: 'NETWORK', id });
        return;
      case 'reset-view':
        commandMap({ kind: 'RESET' });
        return;
      case 'fit-friendly':
        commandMap({ kind: 'FIT', side: 'BLU' });
        return;
      case 'fit-threats':
        commandMap({ kind: 'FIT', side: 'OPF' });
        return;

      // --- the builder ------------------------------------------------
      case 'set-asset':
        fillSlot('asset', id);
        return;
      case 'set-target': {
        const target = props.view === null ? null : targetForSubject(props.view, id);
        if (target === null) showToast(id + ' CANNOT BE A TARGET', 'warn');
        else fillTarget(target);
        return;
      }
      case 'draft-from':
        setBuilder({ asset: id, mission: null, target: null, params: {} });
        armSlot('mission');
        return;
      case 'draft-against': {
        const against = props.view === null ? null : targetForSubject(props.view, id);
        if (against === null) return;
        setBuilder({ asset: null, mission: null, target: against, params: {} });
        armSlot('asset');
        return;
      }

      // --- orders standing against the unit the menu was opened on ------
      case 'edit-order': {
        const first = ordersOn(id)[0];
        if (first !== undefined) editOrder(first.key);
        return;
      }
      case 'duplicate-order': {
        const first = ordersOn(id)[0];
        if (first !== undefined) duplicateOrder(first.key);
        return;
      }
      case 'cancel-order': {
        const standing = ordersOn(id);
        if (standing.length === 1 && standing[0] !== undefined) {
          requestConfirm({ kind: 'cancel-order', key: standing[0].key });
        } else if (standing.length > 1) {
          requestConfirm({ kind: 'cancel-orders', subject: id });
        }
        return;
      }

      // --- the queue card the menu was opened on -----------------------
      case 'why':
        setWhyKey(id);
        return;
      case 'edit':
        editOrder(id);
        return;
      case 'duplicate':
        duplicateOrder(id);
        return;
      case 'reassign':
        reassignOrder(id);
        return;
      case 'raise':
        moveQueued(id, -1);
        return;
      case 'lower':
        moveQueued(id, 1);
        return;
      case 'cancel':
        requestConfirm({ kind: 'cancel-order', key: id });
        return;
    }
  };

  /**
   * Selecting a bare id, for the intel reader's actor and contact.
   *
   * The kind is decided against the view and never by the caller — the same
   * function the context menu uses (`selectionForId`). An id in none of the four
   * lists selects **nothing at all** rather than a `MISSING` panel: a report is a
   * turn old and naming a destroyed asset is normal, so a click that emptied the
   * rail would punish the operator for reading history.
   */
  function selectSubject(id: string): void {
    const subject = selectionForId(props.view, id);
    if (subject !== null) select(subject);
  }

  /** Queue entries naming a roster subject, for the `unit` menu's three order items. */
  function ordersOn(subjectId: string): readonly { readonly key: string }[] {
    return queue.filter(
      (entry) => entry.order.asset === subjectId || namesTarget(entry.order, subjectId),
    );
  }

  /*
   * What the live region says, recomputed only when one of its four facts
   * changes. `countdown.kind` is a *word* (RUNNING, PAUSED, URGENT, and the
   * not-counting reasons), not the clock, so this text is stable for minutes at
   * a time rather than churning every second.
   */
  const announcement = useMemo(() => {
    const parts = [
      props.phase === null ? null : `TURN ${String(props.phase.turn)}`,
      props.phase === null ? null : PHASE_LABELS[props.phase.phase],
      countdown.kind === 'URGENT' ? 'WINDOW CLOSING' : countdown.note,
      link.token === 'LIVE' ? null : `LINK ${link.token}`,
    ];
    return parts.filter((part): part is string => part !== null && part !== '').join(' · ');
  }, [props.phase, countdown.kind, countdown.note, link.token]);

  /*
   * What each irreversible act costs, in the values that make it real.
   *
   * A confirm that only restates the button is a speed bump; one that says
   * `04:12 LEFT - 3 QUEUED` is the sentence that stops the wrong hand. Both are
   * read at the moment the question is asked and are not live — a stage landing
   * mid-question must not change the number being answered about, which is the
   * same reasoning `mw-release` already carries in the store.
   */
  const leaveDetail = [
    countdown.kind === 'RUNNING' || countdown.kind === 'URGENT' ? `${countdown.clock} LEFT` : null,
    queue.length > 0 ? `${String(queue.length)} QUEUED` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  const forceCloseDetail = (() => {
    const cells = props.team?.cells ?? [];
    const claimed = cells.filter((cell) => cell.claimed);
    const submitted = claimed.filter((cell) => cell.submitted).length;
    const parts = [
      countdown.kind === 'RUNNING' || countdown.kind === 'URGENT'
        ? `${countdown.clock} LEFT`
        : null,
      claimed.length > 0 ? `${String(submitted)} OF ${String(claimed.length)} SUBMITTED` : null,
    ].filter((part): part is string => part !== null);
    return parts.length === 0 ? undefined : parts.join(' · ');
  })();

  return (
    <div
      data-bs-theme={props.theme}
      data-testid="console-shell"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100vh',
        /*
         * Both axes scroll rather than reflow.
         *
         * `minWidth` has been here since Phase 1 and worked: below 1280 the
         * document scrolls and nothing is lost. The height had no floor, so
         * below 720 every fixed band kept its size and the centre pane absorbed
         * the whole difference — measured at 640, the pane went 568 -> 488 and
         * `canScrollY` was false. That is reflow, and the shell's own commitment
         * is that it does not reflow.
         */
        minHeight: CONSOLE_MIN_VIEWPORT.height,
        minWidth: CONSOLE_MIN_VIEWPORT.width,
        overflow: 'hidden',
        background: 'var(--bg)',
        color: 'var(--text)',
      }}
    >
      <ClassificationBar height={20} />

      {/*
        The console's one live region.

        There was none. `Toast` carried `role="status"` and `ErrorNote`
        `role="alert"`, so an alert announced and nothing else did: the turn
        advancing, the phase changing, the window opening, the link dropping and
        the attention dot arriving were all silent to a screen reader, which is
        every state change that matters in a ninety-minute exercise.

        Polite, and deliberately coarse. It carries the turn, the phase, whether
        the window is open and the link state — the four facts that change rarely
        and change everything. It does **not** carry the clock: a live region
        whose text changes every second is a region that talks over the room, and
        that exact bug is why `WINDOW CLOSING` no longer embeds the countdown.
      */}
      <span
        data-testid="console-announce"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          border: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {announcement}
      </span>

      <IdentityRow
        seat={props.seat}
        sides={props.sides}
        scenario={props.scenario}
        theme={props.theme}
        onToggleTheme={props.onToggleTheme}
        onRequestLeave={() => requestConfirm({ kind: 'leave-seat' })}
        leaveArmed={confirm?.kind === 'leave-seat'}
      />

      {confirm?.kind === 'leave-seat' && (
        <ConfirmInline
          testId="confirm-leave"
          prompt={`Give up ${props.seat.cell}?`}
          {...(leaveDetail === '' ? {} : { detail: leaveDetail })}
          confirmLabel="LEAVE SEAT"
          onConfirm={() => {
            clearConfirm();
            props.onLeave();
          }}
          onCancel={clearConfirm}
        />
      )}

      <StatusRow
        turn={props.phase?.turn ?? null}
        maxTurns={props.phase?.maxTurns ?? null}
        phase={props.phase?.phase ?? null}
        countdown={countdown}
        orders={ordersCount}
        escalation={props.escalation}
        projection={projection}
        commit={commit}
        onCommit={(next) => {
          /*
            The receipt the commit never had.

            Queueing an order raised `ORDER QUEUED`; submitting the whole set —
            far more consequential, and the thing an operator asks "did that go?"
            about — changed a button from amber to grey and said nothing. Under a
            clock in a loud room that is the question worth answering out loud,
            and it is why a seat presses submit twice.
          */
          props.onSubmit(next);
          showToast(next ? 'ORDERS SUBMITTED' : 'ORDERS WITHDRAWN', next ? 'ok' : 'warn');
        }}
        selection={selection}
        onClearSelection={() => select(null)}
      />

      <nav aria-label="Modules" style={{ flex: 'none', display: 'contents' }}>
        <TabStrip tabs={tabs} active={activeTab} onSelect={selectTab} attention={attention} />
      </nav>

      {props.capabilities?.readOnly === true && <ReadOnlyBanner />}
      {props.gm !== null && (
        <GmControlStrip
          status={status}
          busy={props.gm.busy}
          onControl={props.gm.onControl}
          onRequestForceClose={() => requestConfirm({ kind: 'gm-force-close' })}
          forceCloseArmed={confirm?.kind === 'gm-force-close'}
        />
      )}

      {confirm?.kind === 'gm-force-close' && props.gm !== null && (
        <ConfirmInline
          testId="confirm-force-close"
          prompt="Close the window for all seats?"
          {...(forceCloseDetail === undefined ? {} : { detail: forceCloseDetail })}
          confirmLabel="CLOSE WINDOW"
          onConfirm={() => {
            clearConfirm();
            props.gm?.onControl('FORCE_CLOSE');
          }}
          onCancel={clearConfirm}
        />
      )}

      {/* The rails go either side of the centre pane, and the 1 px gaps are the
          design's own rules between panels (design 164). */}
      <div
        data-testid="console-body"
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          background: 'var(--border)',
        }}
      >
        {/*
          The GM has no rails, rather than two empty ones.

          `gm.snapshot` is not a `CellView`: the umpire is never given a roster,
          and nothing on their console can be selected. The rails used to render
          anyway and say `NO PICTURE` and `NOTHING SELECTED` — two of the four
          places the GM console read as broken for one deferred feature. An
          empty state describes a container that could fill; these could not.
        */}
        {!isGm && (
          <RosterRail
            view={props.view}
            width={rails.left}
            maxWidth={rails.leftMax}
            onContextMenu={(row, x, y) => openContextMenu({ subject: 'unit', id: row.id, x, y })}
            onRegionContextMenu={(region, x, y) =>
              openContextMenu({ subject: 'zone', id: region, x, y })
            }
          />
        )}

        <div
          data-testid="centre-pane"
          style={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--canvas)',
          }}
          onContextMenu={(event) => {
            // The canvas subject. Only when the click landed on the pane itself
            // — a right-click inside a module is that module's.
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            openContextMenu({ subject: 'canvas', id: '', x: event.clientX, y: event.clientY });
          }}
        >
          <AlertLane
            alerts={alerts}
            onDismiss={dismissAlert}
            overflow={overflow}
            onHeight={setAlertLaneHeight}
          />
          {/* Under the lane and inside the pane, so it clears the conditional
              chrome bands above and the queue dock below. The offset is the
              lane's measured height, not a constant that an error with a
              correlation id outgrows. */}
          <Toast
            toast={toast}
            onExpire={clearToast}
            top={alertLaneHeight === 0 ? TOAST_TOP : TOAST_TOP + alertLaneHeight + 6}
          />
          {/* The body scrolls; the lane above does not go with it. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {/* All five modules are built. `OtherView` is still reachable — a
                seat with no snapshot yet, and a seat whose tab set resolves to
                none — and that is now the only way to reach it. */}
            {activeTab === 'MW' && props.view !== null ? (
              <MwView
                view={props.view}
                phase={props.phase}
                serverNowMs={props.serverNowMs}
                onAction={props.onMwAction}
                geometry={theatre.status === 'ready' ? theatre.geometry : null}
              />
            ) : activeTab === 'THEATRE' && props.view !== null ? (
              <TheatreView
                view={props.view}
                facade={theatre}
                onContextMenu={(subject, subjectId, x, y) =>
                  openContextMenu({ subject, id: subjectId, x, y })
                }
              />
            ) : activeTab === 'SDA' && props.view !== null && facade.model !== null ? (
              <SdaView
                view={props.view}
                model={facade.model}
                onContextMenu={(subject, subjectId, x, y) =>
                  openContextMenu({ subject, id: subjectId, x, y })
                }
              />
            ) : activeTab === 'CYBER' && props.view !== null && facade.model !== null ? (
              <CyberView
                view={props.view}
                model={facade.model}
                onContextMenu={(subject, subjectId, x, y) =>
                  openContextMenu({ subject, id: subjectId, x, y })
                }
              />
            ) : activeTab === 'INTEL' && props.view !== null ? (
              // Not an arm of `canvas`, which stays three: intel draws no canvas,
              // so the layer panel, the canvas context menu and `MapCommand` have
              // nothing here to act on and are not offered one.
              <IntelView
                phase={props.phase}
                report={props.intel}
                archive={props.intelArchive}
                onRequest={props.onRequestIntel}
                onSelectSubject={selectSubject}
              />
            ) : (
              /*
                A tab that exists with no snapshot behind it is waiting, not
                missing. The GM is the standing case — served `gm.snapshot`, never
                a `CellView` — and a seat in the two seconds before its first view
                arrives is the transient one. Both say `NO PICTURE`, which is what
                the rails have always said for the same condition.
              */
              <OtherView
                tab={activeTab}
                reason={activeTab !== null && props.view === null ? 'NO_PICTURE' : 'NO_MODULE'}
              />
            )}
          </div>

          {/* The dock is the last band of the centre pane and does not scroll
              with it: an order queue that scrolled off the screen under a
              five-minute clock is a queue nobody checks. */}
          <QueueDock
            view={props.view}
            feedback={feedback}
            onContextMenu={(key, x, y) => openContextMenu({ subject: 'order', id: key, x, y })}
          />
        </div>

        {!isGm && (
          <SelectionRail
            view={props.view}
            width={rails.right}
            maxWidth={rails.rightMax}
            canOrder={commit.enabled}
            readOnly={props.capabilities?.readOnly ?? false}
            mwReadingInPane={mwReadingInPane}
            serverNowMs={props.serverNowMs}
            onMwAction={props.onMwAction}
            onPublish={publishContact}
            onContextMenu={(target, x, y) =>
              openContextMenu({
                subject:
                  target.kind === 'REGION'
                    ? 'zone'
                    : target.kind === 'BAND'
                      ? 'band'
                      : target.kind === 'NETWORK'
                        ? 'network'
                        : 'unit',
                id: target.id,
                x,
                y,
              })
            }
          />
        )}
      </div>

      {contextMenu !== null && menuModel !== null && (
        <ContextMenu
          menu={contextMenu}
          model={menuModel}
          onChoose={onContextChoose}
          onClose={closeContextMenu}
        />
      )}

      <StatusBar
        link={link}
        team={teamReadiness(props.team)}
        tab={activeTab}
        hotkeysOpen={hotkeysOpen}
        onToggleHotkeys={() => setHotkeysOpen(!hotkeysOpen)}
      />
    </div>
  );
}

/**
 * The keys this phase can honestly bind (`model/hotkeys.ts`).
 *
 * Phase 1 bound three, Phase 2 three more, and this phase gives targets to the
 * whole `ORDER` section: `D`, `A M T`, `Tab`, `1–9`, `⏎`, `B` and `⌘Z`. `1–5`
 * also selects a domain, as the design's map says, but only while no picker is
 * armed — a number row means "take that row" the moment there is a list to take
 * one from, which is the design's own resolution of the collision.
 *
 * Typing guards are not politeness: `?` is a shifted character on most layouts
 * and every builder key is a letter, so an unguarded handler eats all of them
 * out of the search box and the picker's filter. **Undo sits above the guard**,
 * because the operator can always take the last thing back.
 */
function useHotkeys(args: {
  hotkeysOpen: boolean;
  setHotkeysOpen: (open: boolean) => void;
  onToggleTheme: () => void;
  contextMenuOpen: boolean;
  closeContextMenu: () => void;
  selection: Selection | null;
  clearSelection: () => void;
  togglePin: (selection: Selection) => void;
  toggleCompare: (() => void) | null;
  tabs: readonly ConsoleTab[];
  selectTab: (tab: ConsoleTab) => void;
  /** Null when the seat may not order at all — read-only, or a closed window. */
  builder: {
    open: boolean;
    setOpen: (open: boolean) => void;
    armedSlot: SlotId | null;
    armSlot: (slot: SlotId | null) => void;
    /** What is in the picker's filter box. Empty is what makes `1–9` a shortcut. */
    query: string;
    rows: readonly PickRow[];
    takeRow: (row: PickRow) => void;
    queue: () => void;
    applyBatch: (() => void) | null;
  } | null;
  ladderOpen: boolean;
  setLadderOpen: (open: boolean) => void;
  whyOpen: boolean;
  closeWhy: () => void;
  confirmOpen: boolean;
  clearConfirm: () => void;
  compareOpen: boolean;
  closeCompare: () => void;
  batchArmed: boolean;
  clearBatch: () => void;
  /** Null when nothing on screen can be revealed on a map — then `F` is unbound. */
  focusOnMap: (() => void) | null;
  undo: () => void;
  /** Null off the INTEL tab, where there is no archive for `←` / `→` to walk. */
  stepReport: ((direction: -1 | 1) => void) | null;
}): void {
  const {
    hotkeysOpen,
    setHotkeysOpen,
    onToggleTheme,
    contextMenuOpen,
    closeContextMenu,
    selection,
    clearSelection,
    togglePin,
    toggleCompare,
    tabs,
    selectTab,
    builder,
    ladderOpen,
    setLadderOpen,
    whyOpen,
    closeWhy,
    confirmOpen,
    clearConfirm,
    compareOpen,
    closeCompare,
    batchArmed,
    clearBatch,
    focusOnMap,
    undo,
    stepReport,
  } = args;

  const isTyping = useCallback((target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // Undo sits outside every mode, including a text field: the design's rule,
      // and the reason it is checked before anything else.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
        return;
      }

      // Escape unwinds **one layer at a time** — the design's rule, and the
      // reason it is never destructive. Innermost first. The context menu and an
      // open confirm handle their own Escape in the capture phase and never
      // reach here, which is what keeps this list in one order rather than two.
      if (event.key === 'Escape') {
        if (contextMenuOpen) {
          closeContextMenu();
          return;
        }
        if (confirmOpen) {
          clearConfirm();
          return;
        }
        if (builder !== null && builder.armedSlot !== null) {
          builder.armSlot(null);
          return;
        }
        if (ladderOpen) {
          setLadderOpen(false);
          return;
        }
        if (whyOpen) {
          closeWhy();
          return;
        }
        if (hotkeysOpen) {
          setHotkeysOpen(false);
          return;
        }
        if (compareOpen) {
          closeCompare();
          return;
        }
        if (batchArmed) {
          clearBatch();
          return;
        }
        if (selection !== null) clearSelection();
        return;
      }

      /*
       * Tab walks the three slots **only while a slot is armed**.
       *
       * It used to fire whenever the builder was open, and the builder is open
       * whenever `commit.enabled` — which is exactly the live orders window. So
       * for the five minutes that matter, a keyboard operator could not Tab to
       * SUBMIT ORDERS, the tab strip, either rail, the queue dock or LEAVE: the
       * console's focus ring worked perfectly before the window opened and was
       * taken away the moment there was something to do with it. The only way
       * out was knowing `d` collapses the builder, three rows below the fold of
       * a hotkey card that could not display itself.
       *
       * Armed is the state where Tab-as-slot-cycling is unambiguous: the picker
       * is open over the rail, focus is already inside it, and "next" plainly
       * means the next slot. With nothing armed, Tab is the browser's again.
       */
      if (event.key === 'Tab' && builder !== null && builder.open && builder.armedSlot !== null) {
        const inPicker = builder.armedSlot !== null;
        if (!isTyping(event.target) || inPicker) {
          event.preventDefault();
          const current = builder.armedSlot === null ? -1 : SLOT_ORDER.indexOf(builder.armedSlot);
          const step = event.shiftKey ? -1 : 1;
          const next =
            SLOT_ORDER[(current + step + SLOT_ORDER.length * 2) % SLOT_ORDER.length] ?? 'asset';
          builder.armSlot(next);
          return;
        }
      }

      // While a picker is armed the number row takes rows out of it, not domains
      // — but **only while its filter is empty**.
      //
      // Phase 3 bound this above the typing guard so it worked from inside the
      // filter box, and every id in the game was letters. Phase 4 introduced ids
      // with digits in them (`MW-S1-1-BLUFOR`), and typing one took a row on the
      // first `1` and closed the picker. Numbers are also the whole vocabulary of
      // the salvo parameter. So the shortcut belongs to the *unfiltered* list,
      // which is the only state in which "the third one" is a stable thing to
      // mean; once the operator is filtering, Enter takes the first and digits
      // are text.
      if (
        builder !== null &&
        builder.armedSlot !== null &&
        builder.query === '' &&
        /^[1-9]$/.test(event.key)
      ) {
        const row = builder.rows[Number(event.key) - 1];
        if (row !== undefined) {
          event.preventDefault();
          builder.takeRow(row);
          return;
        }
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      if (event.key === '?') {
        event.preventDefault();
        setHotkeysOpen(!hotkeysOpen);
        return;
      }
      if (event.key === 'l' || event.key === 'L') {
        event.preventDefault();
        onToggleTheme();
        return;
      }
      if (/^[1-9]$/.test(event.key)) {
        const tab = tabs[Number(event.key) - 1];
        if (tab !== undefined) {
          event.preventDefault();
          selectTab(tab);
        }
        return;
      }
      // `p` needs a selection, `c` needs a pin and `f` needs something a map can
      // place, so each stays unbound until there is one. The arrow stepping is
      // still unbound: it walks the roster, which is a rail's list and not this
      // phase's.
      if ((event.key === 'p' || event.key === 'P') && selection !== null) {
        event.preventDefault();
        togglePin(selection);
        return;
      }
      if ((event.key === 'c' || event.key === 'C') && toggleCompare !== null) {
        event.preventDefault();
        toggleCompare();
        return;
      }
      if ((event.key === 'f' || event.key === 'F') && focusOnMap !== null) {
        event.preventDefault();
        focusOnMap();
        return;
      }
      if (stepReport !== null && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        stepReport(event.key === 'ArrowLeft' ? -1 : 1);
        return;
      }

      if (builder === null) return;
      const key = event.key.toLowerCase();
      if (key === 'd') {
        event.preventDefault();
        builder.setOpen(!builder.open);
        return;
      }
      if (key === 'a' || key === 'm' || key === 't') {
        event.preventDefault();
        const slot: SlotId = key === 'a' ? 'asset' : key === 'm' ? 'mission' : 'target';
        builder.armSlot(builder.armedSlot === slot ? null : slot);
        return;
      }
      if (key === 'b' && builder.applyBatch !== null) {
        event.preventDefault();
        builder.applyBatch();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        builder.queue();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    hotkeysOpen,
    setHotkeysOpen,
    onToggleTheme,
    contextMenuOpen,
    closeContextMenu,
    selection,
    clearSelection,
    togglePin,
    toggleCompare,
    tabs,
    selectTab,
    builder,
    ladderOpen,
    setLadderOpen,
    whyOpen,
    closeWhy,
    confirmOpen,
    clearConfirm,
    compareOpen,
    closeCompare,
    batchArmed,
    clearBatch,
    focusOnMap,
    undo,
    stepReport,
    isTyping,
  ]);
}

/**
 * What `F` and FOCUS ON MAP reveal, and on which board.
 *
 * The console's standing rule for keys (`p` needs a selection, `c` needs a pin):
 * a key bound to a control that cannot act teaches the operator the console is
 * unresponsive. So this returns `null` unless there is a board that has the
 * subject, the seat has that board's tab, and the board is drawing.
 *
 * The orbital board needs no map pack, which is why only the theatre's arm asks
 * about one (`maps/useScenarioFacade.ts`).
 */
function revealOf(
  view: CellViewWire,
  selection: Selection,
  tabs: readonly ConsoleTab[],
  theatre: ScenarioFacadeState,
): { readonly tab: ConsoleTab; readonly command: MapCommandRequest } | null {
  const hasTheatre = tabs.includes('THEATRE') && theatre.status === 'ready';
  const hasSda = tabs.includes('SDA');
  const hasCyber = tabs.includes('CYBER');
  if (selection.kind === 'REGION') {
    return hasTheatre ? { tab: 'THEATRE', command: { kind: 'REGION', id: selection.id } } : null;
  }
  if (selection.kind === 'BAND') {
    return hasSda ? { tab: 'SDA', command: { kind: 'BAND', id: selection.id } } : null;
  }
  if (selection.kind === 'NETWORK') {
    return hasCyber ? { tab: 'CYBER', command: { kind: 'NETWORK', id: selection.id } } : null;
  }
  if (hasTheatre && canPlaceOnTheatre(view, selection.id)) {
    return { tab: 'THEATRE', command: { kind: 'UNIT', id: selection.id } };
  }
  if (hasSda && canPlaceOnOrbit(view, selection.id)) {
    return { tab: 'SDA', command: { kind: 'UNIT', id: selection.id } };
  }
  // **Last, and that ordering is the answer to "which board owns a subject".**
  // The cyber board can place every asset the seat owns, because every asset has
  // a `network` — so asking it first would send `F` on a launcher to a network
  // diagram instead of to the ground it is standing on. Asking it last makes it
  // the board of record for exactly the things the other two cannot place: a
  // cyber team, which is `OFFMAP`, and a machine known only to cyber.
  if (hasCyber && canPlaceOnCyber(view, selection.id)) {
    return { tab: 'CYBER', command: { kind: 'UNIT', id: selection.id } };
  }
  return null;
}

/** Does this order's target name the given roster subject? */
function namesTarget(order: OrderDraft, subjectId: string): boolean {
  const target = order.target;
  switch (target.kind) {
    case 'CONTACT':
      return target.contact === subjectId;
    // M5 Q24's own-asset arm names a friendly id, so a queued HARDEN shows up on
    // the station it hardens exactly as an ENGAGE shows up on the contact.
    case 'OWN_ASSET':
      return target.asset === subjectId;
    case 'REGION':
      return target.region === subjectId;
    case 'BAND':
    case 'SLOT':
      return target.band === subjectId;
    case 'MW_EVENT':
      return target.event === subjectId;
    default:
      return false;
  }
}

/**
 * The subject a context item selects.
 *
 * A `zone` menu always names a region. A `unit` menu names an asset or a contact
 * and the view says which — never the caller, which would be the one place a
 * contact could acquire an asset's treatment.
 */
function selectionForMenu(
  view: CellViewWire | null,
  subject: ContextSubject,
  id: string,
): Selection | null {
  if (subject === 'zone') return { kind: 'REGION', id };
  if (subject === 'band') return { kind: 'BAND', id };
  if (subject === 'network') return { kind: 'NETWORK', id };
  // The id resolution itself lifted to `model/selection.ts` in Phase 8, when the
  // intel reader became its second caller.
  return selectionForId(view, id);
}

/** The zone "FILTER ROSTER TO X" arms — the row's own location word. */
function zoneForMenu(
  view: CellViewWire | null,
  subject: ContextSubject,
  id: string,
): string | null {
  // A band is its own facet value, and `zoneOf` already files an orbital asset
  // under it — so FILTER ROSTER TO GEO arms the same word the rail groups by.
  // An enclave is its own facet value too, and `rosterRow` already files a cyber
  // asset under its `network` — so FILTER ROSTER TO BLUE-CORE arms the word the
  // rail groups the CYBER TEAMS block by.
  if (subject === 'zone' || subject === 'band' || subject === 'network') return id;
  if (view === null) return null;
  const asset = view.friendly.find((candidate) => candidate.id === id);
  if (asset !== undefined) {
    return asset.segment === 'CYBER' ? asset.network : zoneOf(asset.location);
  }
  const contact = view.contacts.find((candidate) => candidate.id === id);
  return contact === undefined ? null : zoneOf(contact.location);
}
